import { addDoc, collection, updateDoc, doc, Timestamp, getDocs, setDoc, deleteDoc } from "@firebase/firestore";
import { getDownloadURL, uploadBytes, ref, deleteObject } from "@firebase/storage";
import { track } from "../../../utils/analytics";

const PLACEHOLDER_IMAGE = "https://firebasestorage.googleapis.com/v0/b/spenza-recipe-app.appspot.com/o/placeholders%2Ficon.png?alt=media&token=5fb6307c-3844-4486-b273-7a53d19b66cc";

async function uploadFile(storage, uid, file, postID) {
    const fileName = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    const storageRef = ref(storage, `posts/${uid}/${postID}/${fileName}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
}

// Best-effort delete of a previous Storage image. Ignores the placeholder and
// external (e.g. Pexels) URLs that aren't in our bucket.
async function deleteImage(storage, imageURL) {
    if (!imageURL || imageURL === PLACEHOLDER_IMAGE) return;
    if (!imageURL.includes('firebasestorage.googleapis.com')) return;
    try {
        await deleteObject(ref(storage, imageURL));
    } catch (e) {
        console.log('deleteImage failed (non-critical):', e);
    }
}

/**
 * Create a new recipe post from the wizard's form data. Shared by the "Save"
 * action on the details page and the "Save" action on the cooking-steps page,
 * so both produce identical documents.
 *
 * @returns {Promise<{ postID?: string, duplicate?: boolean }>}
 */
export async function createRecipe({ db, storage, user, formData, ingredients = [], steps = [] }) {
    const { uid } = user.currentUser;

    // Duplicate-title guard (same as the cooking-steps page).
    const existingPosts = await getDocs(collection(db, 'users', uid, 'posts'));
    const duplicate = existingPosts.docs.find(
        (postDoc) => postDoc.data().postRecipeTitle?.toLowerCase().trim() === formData.postRecipeTitle?.toLowerCase().trim()
    );
    if (duplicate) {
        return { duplicate: true };
    }

    const safeIngredients = (ingredients && ingredients.length)
        ? ingredients
        : [{ ingredientQty: "", ingredientUnit: "", ingredientText: "" }];

    // Cover photo: uploaded file > imported/suggested URL > placeholder. A
    // suggested URL or the placeholder needs no upload, so write it in the
    // initial doc — otherwise the recipe appears in the list a beat before its
    // photo (a deferred second write) and the image "pops in" late.
    const initialImage = formData.coverPhoto
        ? PLACEHOLDER_IMAGE
        : (formData.postImageUrl || PLACEHOLDER_IMAGE);

    const postID = await addDoc(collection(db, "users", uid, "posts"), {
        postRecipeTitle: formData.postRecipeTitle,
        postDescription: formData.postDescription,
        ingredients: safeIngredients,
        steps: [],
        postDuration: formData.postDuration ? `${formData.postDuration} mins` : "10 mins",
        postTags: formData.postTags || [],
        postImageUrl: initialImage,
        authorUid: uid,
        authorName: user.storedUser ? user.storedUser.name : "",
        postDateCreated: Timestamp.now(),
        postDateUpdated: Timestamp.now(),
        postIsSecret: !!formData.postIsSecret,
    }).then((docRef) => docRef.id);

    // Only an uploaded file needs the deferred write (it must upload first).
    if (formData.coverPhoto) {
        const coverPhoto = await uploadFile(storage, uid, formData.coverPhoto[0], postID);
        await updateDoc(doc(db, "users", uid, "posts", postID), { postImageUrl: coverPhoto });
    }

    // Steps — upload all step images in parallel, then write in one updateDoc.
    const newSteps = steps.length > 0
        ? await Promise.all(steps.map(async (step) => {
            const stepsImage = (step.stepsImageFile && step.stepsImageFile !== "")
                ? await uploadFile(storage, uid, step.stepsImageFile, postID)
                : "";
            return { stepsImage, stepsText: step.stepsText };
        }))
        : [{ stepsImage: "", stepsText: "" }];
    await updateDoc(doc(db, "users", uid, "posts", postID), { steps: newSteps });

    // Search index + publicUsers — best-effort, run in parallel, never fail the
    // save. Secret recipes are never indexed, so they can't surface in other
    // users' search results.
    const searchWrites = formData.postIsSecret
        ? []
        : [setDoc(doc(db, "algoliaPostSearch", postID), {
            postRecipeTitle: formData.postRecipeTitle,
            authorUid: uid,
            postDocId: postID,
        })];
    await Promise.allSettled([
        ...searchWrites,
        setDoc(doc(db, 'publicUsers', uid), {
            uid,
            name: user.storedUser ? user.storedUser.name : '',
            dpUrl: user.storedUser ? (user.storedUser.dpUrl || '') : '',
            recipeCount: existingPosts.size + 1,
            updatedAt: Timestamp.now(),
        }),
    ]);

    track('recipe_created', { ingredients: safeIngredients.length });
    return { postID };
}

/**
 * Duplicate an existing recipe into a new post owned by the current user.
 * Copies title/description/duration/tags/ingredients/steps and reuses the same
 * image URLs (no re-upload) with fresh timestamps. The new title is made
 * distinct automatically ("X (Copy)", then "X (Copy 2)", …) so the copy always
 * saves; the user can rename it afterwards in the editor.
 *
 * @param source  the recipe being duplicated (its fields, e.g. from ViewRecipe)
 * @returns {Promise<{ postID: string, title: string }>}
 */
export async function duplicateRecipe({ db, user, source }) {
    const { uid } = user.currentUser;

    // Build a distinct title against the user's existing recipes.
    const existingPosts = await getDocs(collection(db, 'users', uid, 'posts'));
    const existingTitles = new Set(
        existingPosts.docs
            .map((d) => (d.data().postRecipeTitle || '').toLowerCase().trim())
            .filter(Boolean)
    );
    const base = (source.postRecipeTitle || 'Recipe').trim();
    let title = `${base} (Copy)`;
    let n = 2;
    while (existingTitles.has(title.toLowerCase())) {
        title = `${base} (Copy ${n})`;
        n += 1;
    }

    const ingredients = (source.ingredients && source.ingredients.length)
        ? source.ingredients
        : [{ ingredientQty: "", ingredientUnit: "", ingredientText: "" }];
    // Preserve existing step image URLs (no re-upload for a copy).
    const steps = (source.steps && source.steps.length)
        ? source.steps.map((s) => ({ stepsImage: s.stepsImage || "", stepsText: s.stepsText || "" }))
        : [{ stepsImage: "", stepsText: "" }];

    const postID = await addDoc(collection(db, "users", uid, "posts"), {
        postRecipeTitle: title,
        postDescription: source.postDescription || "",
        ingredients,
        steps,
        postDuration: source.postDuration || "10 mins",
        postTags: source.postTags || [],
        postImageUrl: source.postImageUrl || PLACEHOLDER_IMAGE,
        authorUid: uid,
        authorName: user.storedUser ? user.storedUser.name : "",
        postDateCreated: Timestamp.now(),
        postDateUpdated: Timestamp.now(),
        postIsSecret: !!source.postIsSecret,
    }).then((docRef) => docRef.id);

    // Search index + publicUsers count — best-effort, never fail the copy.
    // A duplicate of a secret recipe stays secret, so skip indexing it too.
    const searchWrites = source.postIsSecret
        ? []
        : [setDoc(doc(db, "algoliaPostSearch", postID), {
            postRecipeTitle: title,
            authorUid: uid,
            postDocId: postID,
        })];
    await Promise.allSettled([
        ...searchWrites,
        setDoc(doc(db, 'publicUsers', uid), {
            uid,
            name: user.storedUser ? user.storedUser.name : '',
            dpUrl: user.storedUser ? (user.storedUser.dpUrl || '') : '',
            recipeCount: existingPosts.size + 1,
            updatedAt: Timestamp.now(),
        }, { merge: true }),
    ]);

    track('recipe_duplicated');
    return { postID, title };
}

/**
 * Update an existing recipe's details (title/description/duration/tags + cover
 * photo) without touching its ingredients or steps. Used by the "Save" action
 * on the edit details page.
 *
 * @param details { postRecipeTitle, postDescription, postDuration, postTags }
 * @param coverFile  newly-uploaded File (or null)
 * @param suggestedUrl  a chosen Pexels URL (or null)
 */
export async function updateRecipeDetails({ db, storage, user, recipe, details, coverFile, suggestedUrl }) {
    const { uid } = user.currentUser;
    const recipeRef = doc(db, "users", uid, "posts", recipe.id);

    const isSecret = !!details.postIsSecret;
    await updateDoc(recipeRef, {
        postRecipeTitle: details.postRecipeTitle,
        postDescription: details.postDescription,
        postDuration: `${details.postDuration} mins`,
        postTags: details.postTags || [],
        postDateUpdated: Timestamp.now(),
        postIsSecret: isSecret,
    });

    if (coverFile) {
        await deleteImage(storage, recipe.postImageUrl);
        const url = await uploadFile(storage, uid, coverFile, recipe.id);
        await updateDoc(recipeRef, { postImageUrl: url });
    } else if (suggestedUrl) {
        await deleteImage(storage, recipe.postImageUrl);
        await updateDoc(recipeRef, { postImageUrl: suggestedUrl });
    }

    // Keep the Algolia mirror (collection algoliaPostSearch — NOT
    // algoliaSearchPost, a previous typo here meant title edits never
    // actually reached the real mirror doc) in sync: remove it entirely once
    // secret, upsert it otherwise.
    try {
        if (isSecret) {
            await deleteDoc(doc(db, 'algoliaPostSearch', recipe.id));
        } else if (isSecret !== !!recipe.postIsSecret || details.postRecipeTitle !== recipe.postRecipeTitle) {
            await setDoc(doc(db, 'algoliaPostSearch', recipe.id), {
                postRecipeTitle: details.postRecipeTitle,
                authorUid: uid,
                postDocId: recipe.id,
            }, { merge: true });
        }
    } catch (e) {
        console.log('algolia update failed (non-critical):', e);
    }
}

export default createRecipe;
